import React, { useMemo, useState } from 'react';
import { App, Button, Card, Checkbox, Col, Form, Input, InputNumber, Radio, Result, Row, Spin, Typography, Upload } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import hrmsService from '@/services/hrms.service';
import { formatMoney } from '@/utils/format';

const { Title, Paragraph, Text } = Typography;
const MAX_CV_BYTES = 10 * 1024 * 1024;
const CV_ACCEPT = '.pdf,.doc,.docx,.png,.jpg,.jpeg';

type Question = { code: string; text: string; type?: 'text' | 'yesno' | 'number'; required?: boolean };

function parseQuestions(v: unknown): Question[] {
  if (Array.isArray(v)) return v as Question[];
  if (typeof v === 'string' && v.trim()) {
    try {
      const p = JSON.parse(v);
      return Array.isArray(p) ? p : [];
    } catch {
      return [];
    }
  }
  return [];
}

const CareersJobPage: React.FC = () => {
  const { slug, jobSlug } = useParams<{ slug: string; jobSlug: string }>();
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [cv, setCv] = useState<UploadFile[]>([]);
  const [submitted, setSubmitted] = useState<{ application_number?: string } | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['public-careers-job', slug, jobSlug],
    queryFn: () => hrmsService.getPublicJob(slug!, jobSlug!),
    enabled: Boolean(slug && jobSlug),
  });
  const questions = useMemo(() => parseQuestions(data?.job?.questions), [data]);

  const onFinish = async (values: any) => {
    if (!slug || !jobSlug) return;
    setSubmitting(true);
    try {
      const answers = Object.fromEntries(
        questions.map((q) => [q.code, values.answers?.[q.code]]).filter(([, v]) => v != null && String(v).trim() !== '')
      );
      const file = cv[0]?.originFileObj as File | undefined;
      const result = await hrmsService.publicApply(
        slug,
        jobSlug,
        {
          first_name: values.first_name,
          last_name: values.last_name,
          email: values.email,
          phone: values.phone,
          nationality: values.nationality,
          current_location: values.current_location,
          cover_letter: values.cover_letter,
          consent: !!values.consent,
          honeypot: values.website || '',
          answers: Object.keys(answers).length ? answers : undefined,
        },
        file
      );
      setSubmitted(result);
      form.resetFields();
      setCv([]);
    } catch (e: any) {
      message.error(e?.response?.data?.message || e?.message || 'Application failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error || !data?.job) {
    return (
      <div style={{ maxWidth: 720, margin: '48px auto', padding: 24 }}>
        <Result
          status="404"
          title="This job is no longer accepting applications"
          extra={slug ? <Link to={`/careers/${slug}`}>See other openings</Link> : undefined}
        />
      </div>
    );
  }

  const job = data.job;
  const settings = data.settings || {};
  const consentText = settings.consent_text || 'I consent to the processing of my personal data for recruitment purposes.';

  if (submitted) {
    return (
      <div style={{ maxWidth: 640, margin: '48px auto', padding: 24 }}>
        <Result
          status="success"
          title="Application submitted"
          subTitle={
            submitted.application_number
              ? `Your reference is ${submitted.application_number}. We will contact you if your profile matches the role.`
              : 'Thank you for applying.'
          }
          extra={
            <Link to={`/careers/${slug}`}>
              <Button type="primary">Back to careers</Button>
            </Link>
          }
        />
      </div>
    );
  }

  const salaryOpts = { currency: job.currency || 'AED', minimumFractionDigits: 0, maximumFractionDigits: 0 };
  const salary =
    job.salary_visibility === 'Range' && job.salary_min != null
      ? `${formatMoney(job.salary_min, salaryOpts)} – ${formatMoney(job.salary_max, { ...salaryOpts, display: 'plain' })}`
      : job.salary_visibility === 'Exact' && job.salary_min != null
        ? formatMoney(job.salary_min, salaryOpts)
        : null;

  return (
    <div style={{ maxWidth: 820, margin: '0 auto', padding: '32px 24px' }}>
      <Text type="secondary">
        <Link to={`/careers/${slug}`}>{settings.brand_title || 'Careers'}</Link>
      </Text>
      <Title level={2} style={{ marginTop: 8 }}>
        {job.title}
      </Title>
      <Paragraph type="secondary">
        {[
          job.location_text,
          job.employment_type,
          job.openings ? `${job.openings} opening(s)` : null,
          salary,
          job.closing_date ? `Closes ${String(job.closing_date).slice(0, 10)}` : null,
        ]
          .filter(Boolean)
          .join(' · ')}
      </Paragraph>

      {job.description && (
        <Card title="Description" size="small" style={{ marginBottom: 16 }}>
          <Paragraph style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}>{job.description}</Paragraph>
        </Card>
      )}
      {job.requirements && (
        <Card title="Requirements" size="small" style={{ marginBottom: 16 }}>
          <Paragraph style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}>{job.requirements}</Paragraph>
        </Card>
      )}

      <Card title="Apply for this role">
        <Form form={form} layout="vertical" onFinish={onFinish} requiredMark="optional">
          <Form.Item name="website" label="Website" style={{ position: 'absolute', left: -9999, opacity: 0, height: 0, overflow: 'hidden' }} aria-hidden>
            <Input tabIndex={-1} autoComplete="off" />
          </Form.Item>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="first_name" label="First name" rules={[{ required: true, whitespace: true }]}>
                <Input autoComplete="given-name" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="last_name" label="Last name" rules={[{ required: true, whitespace: true }]}>
                <Input autoComplete="family-name" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="email"
                label="Email"
                dependencies={['phone']}
                rules={[
                  { type: 'email', message: 'Enter a valid email address' },
                  ({ getFieldValue }) => ({
                    validator: (_, v) =>
                      v || getFieldValue('phone') ? Promise.resolve() : Promise.reject(new Error('Enter an email or a phone number')),
                  }),
                ]}
              >
                <Input autoComplete="email" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="phone" label="Phone" dependencies={['email']}>
                <Input autoComplete="tel" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="nationality" label="Nationality">
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="current_location" label="Current location">
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="CV / résumé" extra="PDF, Word or image, up to 10 MB.">
            <Upload
              accept={CV_ACCEPT}
              maxCount={1}
              fileList={cv}
              beforeUpload={(file) => {
                if (file.size > MAX_CV_BYTES) {
                  message.error('File size exceeds 10MB limit');
                  return Upload.LIST_IGNORE;
                }
                return false;
              }}
              onChange={({ fileList }) => setCv(fileList.slice(-1))}
            >
              <Button icon={<UploadOutlined />}>Attach CV</Button>
            </Upload>
          </Form.Item>

          {questions.length > 0 && (
            <Card size="small" type="inner" title="Screening questions" style={{ marginBottom: 16 }}>
              {questions.map((q) => (
                <Form.Item
                  key={q.code}
                  name={['answers', q.code]}
                  label={q.text}
                  rules={q.required ? [{ required: true, message: 'Please answer this question' }] : undefined}
                >
                  {q.type === 'yesno' ? (
                    <Radio.Group
                      options={[
                        { value: 'Yes', label: 'Yes' },
                        { value: 'No', label: 'No' },
                      ]}
                    />
                  ) : q.type === 'number' ? (
                    <InputNumber style={{ width: 200 }} />
                  ) : (
                    <Input.TextArea rows={2} />
                  )}
                </Form.Item>
              ))}
            </Card>
          )}

          <Form.Item name="cover_letter" label="Cover letter">
            <Input.TextArea rows={4} />
          </Form.Item>
          <Form.Item
            name="consent"
            valuePropName="checked"
            rules={[{ validator: (_, v) => (v ? Promise.resolve() : Promise.reject(new Error('Consent is required'))) }]}
          >
            <Checkbox>{consentText}</Checkbox>
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={submitting} block size="large">
            Submit application
          </Button>
        </Form>
      </Card>
    </div>
  );
};

export default CareersJobPage;
